import React, { useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  ALL_INDUSTRY_LPS,
  HeroLeadPart,
  IndustryKey,
  IndustryLandingConfig,
} from '../data/industryLanding';
import { DeckDownloadCTA } from '../components/DeckDownloadCTA';

// 業種別LP（建設・製造・物流）
//
// Claude Design で生成した 3 LP（docs/lp/results/*.html）を React に移植した
// 単一コンポーネント。データは data/industryLanding.ts に集約しており、
// industryKey を切り替えるだけで内容が差し替わる。
//
// CSS: ブランドトークン（hero-bg gradient / cta-primary / brand-card 等）は
// このファイル内で <style> として inline。Astro blog のように global CSS
// にすると SPA の他画面に染み出すため、コンポーネントスコープに留める。

interface Props {
  industryKey: IndustryKey;
  /** Hero CTA「今すぐ30秒で試す」と Final CTA からデモへ */
  onTryDemo: () => void;
  /** Hero / Final CTA の「料金プランを見る」 / Header nav 「料金」 */
  onGoPricing: () => void;
  /** ヘッダーロゴ → トップへ */
  onGoHome: () => void;
  /** 関連ブログのリンク先（既定: /blog/{slug}）。テスト用に上書き可。 */
  blogUrlBuilder?: (slug: string) => string;
  /** 一覧 → /blog/ */
  blogIndexUrl?: string;
}

const defaultBlogUrlBuilder = (slug: string) => `/blog/${slug}`;

export const IndustryLandingView: React.FC<Props> = ({
  industryKey,
  onTryDemo,
  onGoPricing,
  onGoHome,
  blogUrlBuilder = defaultBlogUrlBuilder,
  blogIndexUrl = '/blog/',
}) => {
  const config = ALL_INDUSTRY_LPS[industryKey];
  const rootRef = useRef<HTMLDivElement | null>(null);

  // スクロール表示アニメーション (.reveal → .in)
  useEffect(() => {
    if (!rootRef.current) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    rootRef.current.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [industryKey]);

  // ページタイトルと description を切替（client-side。SEO用の <head> は
  // static HTML 側で出力済み。これは SPA で遷移してきたケースのため）
  useEffect(() => {
    const prevTitle = document.title;
    const prevDescMeta = document.querySelector('meta[name="description"]');
    const prevDesc = prevDescMeta?.getAttribute('content') ?? null;
    document.title = config.metaTitle;
    if (prevDescMeta) prevDescMeta.setAttribute('content', config.metaDescription);
    return () => {
      document.title = prevTitle;
      if (prevDescMeta && prevDesc !== null) prevDescMeta.setAttribute('content', prevDesc);
    };
  }, [config]);

  return (
    <div ref={rootRef} className="industry-lp" style={industryCssVars(config)}>
      <IndustryLandingStyle />

      {/* Header */}
      <header className="ilp-header">
        <div className="ilp-container ilp-header-inner">
          <button
            type="button"
            onClick={onGoHome}
            className="ilp-logo"
            aria-label="安全ポスト＋ トップへ"
          >
            <span className="ilp-logo-mark">安</span>
            安全ポスト<span className="ilp-accent-text">＋</span>
          </button>
          <nav className="ilp-nav">
            <a href="#pain">現場の課題</a>
            <a href="#steps">3ステップ</a>
            <a href="#blog">関連記事</a>
            <button type="button" onClick={onGoPricing} className="ilp-nav-btn">料金</button>
          </nav>
          <button
            type="button"
            onClick={onTryDemo}
            className="cta-primary ilp-header-cta"
          >
            30秒で試す
          </button>
        </div>
      </header>

      <main className="ilp-container">
        {/* Hero */}
        <section className={`${config.heroBgClass} ilp-hero`}>
          <span
            className="ind-pill"
            style={{
              background: config.pillBg,
              color: config.pillTextColor,
            }}
          >
            <span className="ind-dot" style={{ background: config.pillDotColor }}></span>
            {config.pillLabel}
          </span>
          <h1 className="ilp-h1">
            {config.heroH1Line1}
            <br className="hide-on-sm" />
            <span className="ilp-accent-bright">{config.heroH1Accent}</span>
            {config.heroH1Line2}
          </h1>
          <p className="ilp-hero-lead">{renderHeroLead(config.heroLeadParts)}</p>
          <div className="ilp-cta-row">
            <button type="button" onClick={onTryDemo} className="cta-primary ilp-cta-large">
              今すぐ30秒で試す
              <ChevronRight size={18} className="ml-1" aria-hidden />
            </button>
            <button type="button" onClick={onGoPricing} className="cta-secondary ilp-cta-large">
              料金プランを見る
            </button>
          </div>
          <div className="ilp-trust-row">
            <span className="trust-chip">無料で試せる</span>
            <span className="trust-chip">登録・アプリ不要</span>
            <span className="trust-chip">匿名で投稿OK</span>
            <span className="trust-chip">多言語対応</span>
          </div>
        </section>

        {/* Product showcase */}
        <section className="ilp-section reveal">
          <div className="panel ilp-panel">
            <div className="ilp-showcase">
              {/* Phone mock */}
              <div className="ilp-showcase-phone">
                <div className="device">
                  <div className="device-notch"></div>
                  <div className="device-screen">
                    <div className="app-bar">
                      <span className="text-sm font-bold">ヒヤリハット報告</span>
                      <span className="qr-glyph">
                        {Array.from({ length: 9 }).map((_, i) => (
                          <i key={i}></i>
                        ))}
                      </span>
                    </div>
                    <div className="device-body">
                      <p className="device-prompt">どんな危険でしたか？</p>
                      <div className="device-tags">
                        <span
                          className="device-tag-accent"
                          style={{
                            background: config.phoneTagAccent.bg,
                            color: config.phoneTagAccent.color,
                          }}
                        >
                          {config.phoneTagAccent.text}
                        </span>
                        <span className="device-tag-other">{config.phoneTagOther1}</span>
                        <span className="device-tag-other">{config.phoneTagOther2}</span>
                      </div>
                      <div className="mock-input">
                        <p>{config.phoneSampleText}</p>
                      </div>
                      <div className="device-photo">📷 写真を添付</div>
                      <div className="cta-primary device-send">送信する（匿名）</div>
                      <p className="device-foot">所要 約30秒・氏名は不要</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Copy + dashboard mock */}
              <div className="ilp-showcase-copy">
                <p className="ilp-eyebrow ind-num">投稿した先で、何が起きるか</p>
                <h2 className="ilp-h2">
                  QRで送った1件が、
                  <br className="hide-on-sm" />
                  AIで“傾向”に変わる。
                </h2>
                <p className="ilp-body">{config.fourMLead}</p>
                <div className="brand-card ilp-4m-card">
                  <div className="ilp-4m-head">
                    <span>AI 4M分析</span>
                    <span className="ilp-4m-note">今週の報告 / 画面イメージ</span>
                  </div>
                  <div className="ilp-4m-bars">
                    {config.fourMBars.map((b) => (
                      <div key={b.label}>
                        <div className="ilp-4m-row">
                          <span>{b.label}</span>
                          <span>{b.percentText}</span>
                        </div>
                        <div className="bar-track">
                          <div
                            className={b.isAccent ? 'bar-fill ind-bar' : 'bar-fill'}
                            style={{
                              width: `${b.widthPercent}%`,
                              ...(b.isAccent ? {} : { background: b.fallbackColor }),
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats band */}
        <section className="ilp-section reveal">
          <div className="ilp-center mb-8">
            <p className="ilp-eyebrow ind-num">{config.statsEyebrow}</p>
            <h2 className="ilp-h2">{config.statsHeading}</h2>
          </div>
          <div className="ilp-stats-grid">
            {config.stats.map((s, i) => (
              <div key={i} className="brand-card ilp-stat-card">
                <div className="ilp-stat-big ind-num">
                  {s.big}
                  {s.unit && <span className="ilp-stat-unit">{s.unit}</span>}
                  {s.bigSuffix && <span className="ilp-stat-suffix">{s.bigSuffix}</span>}
                </div>
                <p className="ilp-stat-desc">{renderMultiline(s.description)}</p>
                <p className="ilp-stat-note">{s.note}</p>
              </div>
            ))}
          </div>
          <p className="ilp-source">
            出典：厚生労働省「令和6年 労働災害発生状況」（2025年公表・確定値）
          </p>
        </section>

        {/* Pain points */}
        <section id="pain" className="ilp-section reveal ilp-scroll-anchor">
          <div className="ilp-center mb-8">
            <p className="ilp-eyebrow ind-num">{config.painEyebrow}</p>
            <h2 className="ilp-h2">{config.painHeading}</h2>
          </div>
          <div className="ilp-cards-3">
            {config.painPoints.map((p, i) => (
              <div key={i} className="brand-card ilp-pain-card">
                <div className="ilp-pain-num ind-num">課題 {String(i + 1).padStart(2, '0')}</div>
                <h3 className="ilp-pain-title">{p.title}</h3>
                <p className="ilp-pain-body">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Solution: 3 steps */}
        <section id="steps" className="ilp-section reveal ilp-scroll-anchor">
          <div className="ilp-center mb-10">
            <p className="ilp-eyebrow ind-num">{config.stepsEyebrow}</p>
            <h2 className="ilp-h2">{config.stepsHeading}</h2>
          </div>
          <div className="ilp-cards-3">
            {config.steps.map((s, i) => (
              <div key={i} className="brand-card ilp-step-card">
                <div className="step-badge">{['①', '②', '③'][i]}</div>
                <h3 className="ilp-step-title">{s.title}</h3>
                <p className="ilp-step-body">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related blog */}
        <section id="blog" className="ilp-section reveal ilp-scroll-anchor">
          <div className="ilp-blog-head">
            <h2 className="ilp-blog-heading">{config.blogHeading}</h2>
            <a href={blogIndexUrl} className="ilp-blog-all ind-num">
              記事一覧 →
            </a>
          </div>
          <div className="ilp-blog-grid">
            {config.blogLinks.map((b) => (
              <a key={b.slug} href={blogUrlBuilder(b.slug)} className="blog-row">
                {b.text}
              </a>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section id="cta" className="ilp-section reveal ilp-scroll-anchor">
          <div className={`${config.heroBgClass} ilp-final-cta`}>
            <h2 className="ilp-final-h2">
              {config.ctaHeadingLine1}
              <span className="ilp-accent-bright">{config.ctaHeadingAccent}</span>
              {config.ctaHeadingLine2}
            </h2>
            <p className="ilp-final-body">{config.ctaBody}</p>
            <div className="ilp-cta-row ilp-cta-row-final">
              <button type="button" onClick={onTryDemo} className="cta-primary ilp-cta-large">
                今すぐ30秒で試す
                <ChevronRight size={18} className="ml-1" aria-hidden />
              </button>
              <button type="button" onClick={onGoPricing} className="cta-secondary ilp-cta-large">
                料金プランを見る
              </button>
            </div>
          </div>
        </section>

        {/* 資料DL: 業種別LPからの「持ち帰り検討」需要に応える */}
        <section className="ilp-section ilp-scroll-anchor">
          <DeckDownloadCTA
            placement={`lp-${industryKey}`}
            description={`${config.pillLabel.replace('向け', '')}での活用パターンを含む、サービス概要資料 (A4 14ページ)。社内ご検討にどうぞ。`}
          />
        </section>
      </main>

      <footer className="ilp-footer">
        <div className="ilp-container ilp-footer-inner">
          <div className="ilp-footer-brand">
            <span className="ilp-logo-mark ilp-logo-mark-sm">安</span>
            安全ポスト＋
          </div>
          <p className="ilp-footer-tagline">ヒヤリハット報告 SaaS（QR × AI × 匿名）</p>
          <p className="ilp-footer-source">
            統計出典：厚生労働省「令和6年 労働災害発生状況」
          </p>
        </div>
      </footer>
    </div>
  );
};

// =========================================================
// CSS variables for industry accent
// =========================================================
function industryCssVars(config: IndustryLandingConfig): React.CSSProperties {
  return {
    // CSS カスタムプロパティは React の型では追加属性として扱う
    ['--ind' as string]: config.indColor,
    ['--ind-strong' as string]: config.indStrongColor,
  };
}

function renderHeroLead(parts: HeroLeadPart[]): React.ReactNode {
  return parts.map((p, i) =>
    typeof p === 'string'
      ? <React.Fragment key={i}>{p}</React.Fragment>
      : <strong key={i} className="ilp-hero-lead-em">{p.emphasis}</strong>
  );
}

function renderMultiline(text: string): React.ReactNode {
  const lines = text.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

// =========================================================
// Inline brand stylesheet
// =========================================================
const IndustryLandingStyle: React.FC = () => (
  <style>{`
    .industry-lp {
      --safety-50:  #fff7ed;
      --safety-100: #ffedd5;
      --safety-500: #f97316;
      --safety-600: #ea580c;
      --safety-700: #c2410c;
      --safety-900: #7c2d12;
      --industrial-800: #1e293b;
      --industrial-900: #0f172a;
      --yellow-500: #eab308;
      --yellow-600: #ca8a04;
      font-family: 'Noto Sans JP', -apple-system, BlinkMacSystemFont,
                   'Hiragino Sans', 'Yu Gothic UI', 'Yu Gothic', sans-serif;
      color: var(--industrial-900);
      line-height: 1.7;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
      background: #fff;
      scroll-behavior: smooth;
    }
    .industry-lp * { box-sizing: border-box; }
    .industry-lp .hide-on-sm { display: inline; }
    @media (max-width: 640px) { .industry-lp .hide-on-sm { display: none; } }

    /* Container */
    .ilp-container { max-width: 1120px; margin: 0 auto; padding: 0 1.25rem; }
    .ilp-section { margin-top: 4rem; }
    @media (min-width: 640px) { .ilp-section { margin-top: 5rem; } }
    .ilp-center { text-align: center; }
    .industry-lp .mb-8 { margin-bottom: 2rem; }
    .industry-lp .mb-10 { margin-bottom: 2.5rem; }
    .ilp-scroll-anchor { scroll-margin-top: 5rem; }

    /* Accent text */
    .ilp-accent-text {
      background: linear-gradient(to right, var(--safety-600), var(--yellow-500));
      -webkit-background-clip: text; background-clip: text; color: transparent;
    }
    .ilp-accent-bright {
      background: linear-gradient(to right, #fde68a, #ffffff);
      -webkit-background-clip: text; background-clip: text; color: transparent;
    }

    /* Header */
    .ilp-header {
      position: sticky; top: 0; z-index: 50;
      background: rgba(255,255,255,0.85);
      backdrop-filter: blur(8px);
      border-bottom: 1px solid #e2e8f0;
    }
    .ilp-header-inner {
      height: 4rem;
      display: flex; align-items: center; justify-content: space-between;
      gap: 1rem;
    }
    .ilp-logo {
      display: inline-flex; align-items: center; gap: 0.5rem;
      font-weight: 800; color: var(--industrial-900); font-size: 1.125rem;
      background: none; border: 0; padding: 0; cursor: pointer;
      font-family: inherit;
    }
    .ilp-logo-mark {
      display: inline-block; width: 2rem; height: 2rem; border-radius: 0.5rem;
      background-image: url('/icon-512x512.png');
      background-size: cover; background-position: center;
      /* font-size 0 で span 内の文字 (古い「安」)を非表示にする. img 移植は CSS のみで完結させる. */
      font-size: 0; color: transparent;
    }
    .ilp-logo-mark-sm { width: 1.75rem; height: 1.75rem; }
    .ilp-nav {
      display: none;
      align-items: center; gap: 1.75rem;
      font-size: 0.875rem; font-weight: 500; color: #475569;
    }
    @media (min-width: 768px) { .ilp-nav { display: flex; } }
    .ilp-nav a, .ilp-nav-btn {
      color: #475569; text-decoration: none; transition: color 0.15s;
      background: none; border: 0; cursor: pointer;
      font-family: inherit; font-size: inherit; font-weight: inherit;
      padding: 0;
    }
    .ilp-nav a:hover, .ilp-nav-btn:hover { color: var(--industrial-900); }
    .ilp-header-cta { font-size: 0.875rem; font-weight: 700; padding: 0.625rem 1.25rem; border-radius: 0.75rem; white-space: nowrap; border: 0; cursor: pointer; }

    /* CTA buttons */
    .cta-primary {
      background: linear-gradient(to right, var(--safety-500), var(--yellow-500));
      color: #fff;
      box-shadow: 0 20px 25px -5px rgba(249,115,22,0.25), 0 8px 10px -6px rgba(249,115,22,0.20);
      transition: transform .18s ease, box-shadow .18s ease, background .18s ease;
      border: 0; cursor: pointer; font-family: inherit;
    }
    .cta-primary:hover {
      background: linear-gradient(to right, var(--safety-600), var(--yellow-600));
      transform: translateY(-3px);
      box-shadow: 0 28px 40px -8px rgba(249,115,22,0.35);
    }
    .cta-secondary {
      background: #fff; color: var(--industrial-900);
      border: 1px solid #e2e8f0;
      transition: background .18s ease, transform .18s ease;
      cursor: pointer; font-family: inherit;
    }
    .cta-secondary:hover { background: #f8fafc; transform: translateY(-2px); }
    .ilp-cta-large {
      font-size: 1rem; font-weight: 700; padding: 1rem 2rem;
      border-radius: 0.75rem;
      display: inline-flex; align-items: center; justify-content: center;
    }
    @media (min-width: 640px) { .ilp-cta-large { font-size: 1.125rem; } }
    .ilp-cta-large svg { margin-left: 0.25rem; }

    /* Hero */
    .ilp-hero {
      color: #fff;
      border-radius: 1rem;
      padding: 3.5rem 1.5rem;
      text-align: center;
      margin-top: 1.5rem;
      overflow: hidden;
    }
    @media (min-width: 640px) { .ilp-hero { padding: 4rem 2.5rem; } }
    .hero-con {
      background:
        radial-gradient(circle at 100% 0%, rgba(251,146,60,0.30), transparent 44%),
        radial-gradient(circle at 0% 100%, rgba(234,88,12,0.28), transparent 48%),
        linear-gradient(135deg, #1c1207 0%, #7c2d12 58%, #c2410c 100%);
    }
    .hero-man {
      background:
        radial-gradient(circle at 100% 0%, rgba(96,165,250,0.30), transparent 44%),
        radial-gradient(circle at 0% 100%, rgba(37,99,235,0.30), transparent 48%),
        linear-gradient(135deg, #0b1120 0%, #1e3a8a 58%, #1d4ed8 100%);
    }
    .hero-log {
      background:
        radial-gradient(circle at 100% 0%, rgba(52,211,153,0.28), transparent 44%),
        radial-gradient(circle at 0% 100%, rgba(5,150,105,0.30), transparent 48%),
        linear-gradient(135deg, #05231b 0%, #065f46 58%, #047857 100%);
    }
    .ind-pill {
      display: inline-flex; align-items: center; gap: 0.4rem;
      padding: 0.3rem 0.85rem; border-radius: 9999px;
      font-size: 0.8rem; font-weight: 700;
    }
    .ind-dot { width: 0.5rem; height: 0.5rem; border-radius: 9999px; }
    .ilp-h1 {
      font-weight: 800; letter-spacing: -0.01em; line-height: 1.3;
      font-size: clamp(1.85rem, 5vw, 2.7rem);
      margin: 1.5rem 0 0;
    }
    .ilp-hero-lead {
      margin: 1.25rem auto 0;
      color: #cbd5e1;
      max-width: 42rem;
      font-size: 1rem;
      line-height: 1.7;
    }
    @media (min-width: 640px) { .ilp-hero-lead { font-size: 1.125rem; } }
    .ilp-hero-lead-em { color: #fff; font-weight: 600; }
    .ilp-cta-row {
      margin-top: 2rem;
      display: flex; flex-direction: column; gap: 0.75rem; justify-content: center;
    }
    @media (min-width: 640px) { .ilp-cta-row { flex-direction: row; } }
    .ilp-cta-row-final { margin-top: 2rem; }
    .ilp-trust-row {
      margin-top: 1.75rem;
      display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem;
      justify-content: center;
    }
    .trust-chip {
      display: inline-flex; align-items: center; gap: 0.4rem;
      font-size: 0.8rem; font-weight: 600; color: #e2e8f0;
    }
    .trust-chip::before {
      content: ""; width: 1rem; height: 1rem; flex: none;
      border-radius: 9999px;
      background: rgba(52,211,153,.9);
      -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='black' d='M9 16.2l-3.5-3.5L4 14.2 9 19.2 20 8.2l-1.4-1.4z'/></svg>") center/contain no-repeat;
              mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='black' d='M9 16.2l-3.5-3.5L4 14.2 9 19.2 20 8.2l-1.4-1.4z'/></svg>") center/contain no-repeat;
    }

    /* Cards */
    .brand-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 0.75rem;
      box-shadow: 0 1px 2px 0 rgba(15,23,42,0.05);
      transition: transform .2s ease, box-shadow .2s ease;
    }
    .brand-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 18px 30px -12px rgba(15,23,42,0.18);
    }
    .panel { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 1.25rem; }
    .ilp-panel { padding: 3rem 1.5rem; }
    @media (min-width: 640px) { .ilp-panel { padding: 3rem 2.5rem; } }

    /* Showcase */
    .ilp-showcase {
      display: grid; grid-template-columns: 1fr; gap: 2.5rem; align-items: center;
    }
    @media (min-width: 1024px) {
      .ilp-showcase { grid-template-columns: 1fr 1fr; gap: 3.5rem; }
    }
    .ilp-showcase-phone {
      order: 2; display: flex; justify-content: center;
    }
    .ilp-showcase-copy { order: 1; }
    @media (min-width: 1024px) {
      .ilp-showcase-phone { order: 1; }
      .ilp-showcase-copy { order: 2; }
    }
    .device {
      width: 252px; flex: none;
      border-radius: 2.4rem; background: var(--industrial-900); padding: 9px;
      box-shadow: 0 36px 70px -24px rgba(15,23,42,.5);
    }
    .device-notch { height: 22px; display: flex; align-items: center; justify-content: center; }
    .device-notch::after { content: ""; width: 78px; height: 5px; border-radius: 9999px; background: #334155; }
    .device-screen { background: #fff; border-radius: 1.8rem; overflow: hidden; }
    .app-bar {
      background: linear-gradient(135deg, var(--industrial-900), #1e3a8a);
      color: #fff; padding: 14px 16px; display: flex; align-items: center; justify-content: space-between;
    }
    .app-bar .text-sm { font-size: 0.875rem; }
    .app-bar .font-bold { font-weight: 700; }
    .qr-glyph {
      width: 26px; height: 26px; border-radius: 6px; background: #fff;
      display: grid; grid-template-columns: repeat(3, 1fr); grid-template-rows: repeat(3, 1fr);
      gap: 2px; padding: 4px;
    }
    .qr-glyph i { background: var(--industrial-900); border-radius: 1px; }
    .qr-glyph i:nth-child(2), .qr-glyph i:nth-child(4),
    .qr-glyph i:nth-child(6), .qr-glyph i:nth-child(8) { background: transparent; }
    .device-body { padding: 1rem; }
    .device-prompt { font-size: 0.75rem; font-weight: 700; color: #64748b; margin-bottom: 0.5rem; }
    .device-tags { display: flex; flex-wrap: wrap; gap: 0.375rem; margin-bottom: 0.75rem; }
    .device-tag-accent, .device-tag-other {
      font-size: 11px; font-weight: 600; padding: 0.25rem 0.625rem; border-radius: 9999px;
    }
    .device-tag-other { background: #f1f5f9; color: #64748b; }
    .mock-input {
      background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 10px;
      padding: 0.75rem; margin-bottom: 0.75rem;
    }
    .mock-input p { font-size: 11px; color: #475569; line-height: 1.5; margin: 0; }
    .device-photo { font-size: 11px; color: #94a3b8; margin-bottom: 1rem; }
    .device-send {
      text-align: center; font-size: 0.875rem; font-weight: 700;
      padding: 0.625rem 0; border-radius: 0.75rem;
    }
    .device-foot { font-size: 10px; color: #94a3b8; text-align: center; margin-top: 0.5rem; }

    /* Showcase copy */
    .ilp-eyebrow { font-size: 0.875rem; font-weight: 700; }
    .ind-num { color: var(--ind-strong); }
    .ilp-h2 {
      margin: 0.5rem 0 0;
      font-size: 1.5rem; font-weight: 800;
      color: var(--industrial-900); line-height: 1.4;
    }
    @media (min-width: 640px) { .ilp-h2 { font-size: 1.875rem; } }
    .ilp-body { margin-top: 1rem; color: #475569; line-height: 1.7; }
    .ilp-4m-card { padding: 1.25rem; margin-top: 1.5rem; }
    .ilp-4m-head {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 1rem;
      font-size: 0.875rem; font-weight: 700; color: var(--industrial-900);
    }
    .ilp-4m-note { font-size: 11px; color: #94a3b8; font-weight: 400; }
    .ilp-4m-bars > * + * { margin-top: 0.75rem; }
    .ilp-4m-row {
      display: flex; justify-content: space-between;
      font-size: 0.75rem; color: #64748b; margin-bottom: 0.25rem;
    }
    .bar-track { height: 12px; border-radius: 9999px; background: #eef2f7; overflow: hidden; }
    .bar-fill { height: 100%; border-radius: 9999px; }
    .ind-bar { background: var(--ind); }

    /* Stats */
    .ilp-stats-grid {
      display: grid; grid-template-columns: 1fr; gap: 1rem;
    }
    @media (min-width: 640px) {
      .ilp-stats-grid { grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
    }
    .ilp-stat-card { padding: 1.5rem; text-align: center; }
    .ilp-stat-big {
      font-size: 2.25rem; font-weight: 800; line-height: 1; color: var(--ind-strong);
    }
    @media (min-width: 640px) { .ilp-stat-big { font-size: 3rem; } }
    .ilp-stat-unit { font-size: 1.5rem; vertical-align: top; }
    .ilp-stat-suffix { font-size: 1.5rem; }
    .ilp-stat-desc { margin-top: 0.5rem; font-size: 0.875rem; color: #475569; line-height: 1.7; }
    .ilp-stat-note { margin-top: 0.5rem; font-size: 0.75rem; color: #94a3b8; }
    .ilp-source { margin-top: 1rem; font-size: 0.75rem; color: #94a3b8; text-align: center; }

    /* Pain & Steps */
    .ilp-cards-3 {
      display: grid; grid-template-columns: 1fr; gap: 1.25rem;
    }
    @media (min-width: 768px) {
      .ilp-cards-3 { grid-template-columns: repeat(3, 1fr); }
    }
    .ilp-pain-card { padding: 1.5rem; }
    .ilp-pain-num { font-size: 0.75rem; font-weight: 700; margin-bottom: 0.75rem; }
    .ilp-pain-title { font-size: 1.125rem; font-weight: 700; color: var(--industrial-900); margin: 0; }
    .ilp-pain-body { margin-top: 0.75rem; font-size: 0.875rem; color: #475569; line-height: 1.7; }

    .ilp-step-card { padding: 1.75rem; position: relative; }
    .step-badge {
      background: color-mix(in srgb, var(--ind) 12%, transparent);
      color: var(--ind-strong);
      display: inline-flex; align-items: center; justify-content: center;
      width: 2.75rem; height: 2.75rem; border-radius: 0.75rem;
      font-size: 1.25rem; font-weight: 800;
    }
    .ilp-step-title { margin-top: 1rem; font-size: 1.125rem; font-weight: 700; color: var(--industrial-900); }
    .ilp-step-body { margin-top: 0.75rem; font-size: 0.875rem; color: #475569; line-height: 1.7; }

    /* Blog */
    .ilp-blog-head {
      display: flex; align-items: flex-end; justify-content: space-between;
      margin-bottom: 0.75rem; gap: 1rem; flex-wrap: wrap;
    }
    .ilp-blog-heading { font-size: 1rem; font-weight: 700; color: #64748b; margin: 0; }
    .ilp-blog-all { font-size: 0.875rem; font-weight: 700; text-decoration: none; }
    .ilp-blog-all:hover { text-decoration: underline; }
    .ilp-blog-grid {
      display: grid; grid-template-columns: 1fr; column-gap: 2.5rem;
    }
    @media (min-width: 768px) { .ilp-blog-grid { grid-template-columns: 1fr 1fr; } }
    .blog-row {
      display: flex; align-items: center; gap: 0.65rem;
      padding: 0.7rem 0.25rem;
      border-bottom: 1px solid #eef2f7;
      font-size: 0.9rem; color: #334155;
      transition: color 0.15s ease, padding 0.15s ease;
      text-decoration: none;
    }
    .blog-row:hover { color: var(--ind-strong); padding-left: 0.5rem; }
    .blog-row::before {
      content: ""; width: 6px; height: 6px; border-radius: 9999px;
      background: var(--ind); flex: none;
    }

    /* Final CTA */
    .ilp-final-cta {
      color: #fff;
      border-radius: 1rem;
      padding: 3.5rem 1.5rem;
      text-align: center;
    }
    @media (min-width: 640px) { .ilp-final-cta { padding: 3.5rem 2.5rem; } }
    .ilp-final-h2 {
      font-weight: 800; line-height: 1.3;
      font-size: clamp(1.6rem, 4vw, 2.3rem);
      margin: 0;
    }
    .ilp-final-body { margin-top: 1rem; color: #cbd5e1; max-width: 36rem; margin-left: auto; margin-right: auto; }

    /* Footer */
    .ilp-footer { margin-top: 4rem; border-top: 1px solid #e2e8f0; }
    @media (min-width: 640px) { .ilp-footer { margin-top: 5rem; } }
    .ilp-footer-inner {
      display: flex; flex-direction: column; align-items: center; justify-content: space-between;
      gap: 1rem; padding: 2.5rem 1.25rem;
      font-size: 0.875rem; color: #64748b;
    }
    @media (min-width: 640px) { .ilp-footer-inner { flex-direction: row; } }
    .ilp-footer-brand {
      display: flex; align-items: center; gap: 0.5rem;
      font-weight: 700; color: var(--industrial-900);
    }
    .ilp-footer-tagline { margin: 0; }
    .ilp-footer-source { margin: 0; font-size: 0.75rem; color: #94a3b8; }

    /* Reveal animation */
    .reveal { opacity: 0; transform: translateY(18px); transition: opacity .6s ease, transform .6s ease; }
    .reveal.in { opacity: 1; transform: none; }
  `}</style>
);

export default IndustryLandingView;
